import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentPage } from "../store/navSlice";
import {
  Box,
  Container, keyframes,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  Avatar,
  IconButton,
  InputBase,
  Divider,
  Stack,
  Pagination,
  Modal,
  Fade,
  Backdrop,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
// Import your images
import LogoImage from "../images/decodia-logo.png";
// ─── LOCAL IMAGE IMPORTS ──────────────────────────────────────────────────────
import blog_image1 from "../images/blog_image1.jpeg";
import blog_image_new from "../images/blog_image_new.jpeg";
import secondBlogImage from "../images/Lighting.jpeg";
import ConsultationFormDialog from "./Consultation";
import ChatButton from "../components/freeChatComponent/ChatButton";
import trendsBlogImage from "../images/trendsBlogImage.png";
// ─── ANIMATION KEYFRAMES ──────────────────────────────────────────────────────
const fadeIn = {
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
};

const slideDown = {
  "0%": { opacity: 0, transform: "translateY(-20px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
};

const pulseAnimation = {
  "0%": { transform: "scale(1)" },
  "50%": { transform: "scale(1.05)" },
  "100%": { transform: "scale(1)" },
};

const underline = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;
// ─── colour tokens ──────────────────────────────────────────────────────────
const C = {
  primary: "#FF6B35",
  primaryDark: "#E55A2B",
  primaryLight: "#FF8E53",
  accent: "#EED688",
  dark: "#1a1005",
  mid: "#3d2c0f",
  warm: "#b77446",
  surface: "#fdf8f3",
  card: "#ffffff",
  text: "#1a1005",
  muted: "#7a6550",
  border: "rgba(183,116,70,0.15)",
  gold: "#D4AF37",
};

// ─── font injection ──────────────────────────────────────────────────────────
const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
`;

// ─── static blog data ────────────────────────────────────────────────────────
const CATEGORIES = ["All", "Tips & Tricks", "Trends", "Materials", "Colour", "Spaces", "Renovation", "Budgeting"];

const FULL_FEATURED_CONTENT = [
  {
    heading: "It Shapes How You Feel Every Single Day",
    body: "Your surroundings have a direct impact on your mood, energy, and mental wellbeing. A cluttered, poorly lit room increases stress. A thoughtfully designed space — with the right balance of light, colour, and layout — does the opposite. It calms you, focuses you, and lifts your spirit. This is exactly what professional home interior design in Kolkata addresses. It isn't just about choosing curtain colours. It's about creating an environment where your family thrives. Residential interior designers study how people move through and use a space, then design around those patterns. The result is a home that feels effortless to live in."
  },
  {
    heading: "It Makes Every Square Foot Count",
    body: "Space is precious — especially in a city like Kolkata, where most homes are compact and every room needs to serve multiple purposes. A skilled interior designer in Kolkata knows how to maximise a floor plan without making it feel cramped. Smart storage solutions, multi-functional furniture, strategic lighting — these are the tools of the trade that transform a tight 2BHK into a home that breathes. The same principle applies to commercial interior design in Kolkata. A well-planned office layout improves workflow, reduces wasted movement, and makes employees feel comfortable and productive. For retail stores and restaurants, the right interior design directly influences how long customers stay — and how much they spend."
  },
  {
    heading: "It Reflects Your Identity",
    body: "Whether it's your home or your business, the space you occupy tells a story about who you are. Luxury interior design in Kolkata isn't just about expensive materials — it's about curating an environment that expresses your values, taste, and aspirations with precision. For businesses especially, a professionally designed office or showroom communicates credibility and attention to detail before a single word is spoken. When clients walk into a beautifully designed space, they notice. That first impression is worth more than any business card."
  },
  {
    heading: "It's a Long-Term Financial Investment",
    body: "Many homeowners hesitate at interior design costs in Kolkata, viewing it as an expense. In reality, it's an investment. A well-designed home holds higher resale value. Durable, well-sourced materials outlast cheap alternatives by years. Proper space planning prevents costly renovation mistakes down the line. More importantly, full-service interior design firms in Kolkata manage your entire project — from concept to execution — saving you the hidden cost of mismanaged timelines, wrong material purchases, and contractor coordination headaches."
  },
  {
    heading: "Sustainability Matters Now More Than Ever",
    body: "Modern interior design services in Kolkata increasingly embrace sustainable design — eco-friendly materials, energy-efficient lighting, low-VOC paints, and natural ventilation strategies. A sustainable interior designer in Kolkata doesn't just create a beautiful space — they create one that's healthier for your family and gentler on the environment. In a city dealing with urban heat and air quality challenges, this isn't a trend. It's a responsibility."
  },
  {
    heading: "The Bottom Line",
    body: "Good interior design is not indulgence — it's intelligence. It improves the quality of your daily life, maximises the value of your property, reflects your personality, and — when done sustainably — benefits the world around you. If you've been putting off redesigning your home or office, consider this your sign."
  }
];

const CUSTOM_INTERIORS_CONTENT = [
  {
    heading: "The Problem with Ready-Made Furniture",
    body: "Walk into any large furniture store in Kolkata — or browse an e-commerce platform — and the options look impressive. Clean lines, modern finishes, reasonable prices. It's tempting to pick, pay, and be done with it. But ready-made furniture is designed for average spaces and average lifestyles. It's built to fit into most homes — which means it fits perfectly into none. Standard sizes don't match Indian homes. You sacrifice storage. Nothing connects visually. It wears out faster. The short-term savings often come with long-term regret."
  },
  {
    heading: "What Custom Interiors Actually Mean",
    body: "When people hear 'custom interior design,' they sometimes imagine a luxury-only service with a price tag that's out of reach. That's a myth worth addressing. Custom interiors simply mean that your home is designed around your life — your room dimensions, your daily habits, your family's needs, your taste, and your budget. A skilled residential interior designer in Kolkata doesn't just make your home look beautiful. They think about how you move through the space in the morning, where your children need to study without distraction, how to maximise natural light in a west-facing apartment, where to hide the inverter, the router, and all the cables that clutter modern homes, and how to honour the heritage character of an old South Kolkata home while making it liveable today. Custom design is not a luxury add-on. It is intentional living — made physical."
  },
  {
    heading: "5 Reasons Custom Interiors Feel Better",
    body: "1. Every Inch Is Used With Purpose — Kolkata apartments have walls, corners, and alcoves that standard furniture simply ignores. Custom cabinetry and built-ins are designed to fill every inch, giving you significantly more storage without making the space feel cramped.\n\n2. The Space Tells a Single Story — One of the most immediate differences you notice in a professionally designed custom interior is cohesion. The material of the kitchen shutters echoes the finish on the bedroom wardrobe. The colour palette flows from room to room. This is what separates a home that feels designed from one that feels decorated.\n\n3. It Fits Your Lifestyle, Not Someone Else's — Do you work from home and need a quiet corner with ergonomic shelving? Do you have elderly parents who need grab bars integrated tastefully? Do you love cooking and need a modular kitchen with a dedicated spice zone? Ready-made furniture cannot solve these problems.\n\n4. The Quality Is Built to Last — Custom interiors use materials selected for your specific context — the humidity in your flat, the sunlight exposure on your walls, the wear-and-tear in high-traffic areas.\n\n5. It Adds Real Value to Your Property — A well-executed custom interior is not just an expense — it is an investment. Properties in Kolkata with high-quality custom interiors command significantly higher resale and rental values."
  },
  {
    heading: "The Kolkata Context: Why Custom Design Matters Even More Here",
    body: "Kolkata has a design character unlike any other city in India. Old North Kolkata homes carry intricate woodwork and high ceilings. Heritage apartments in Ballygunge have generous proportions that deserve thoughtful treatment. Newer high-rises in New Town and Rajarhat have compact layouts that demand clever space planning. A generic, off-the-shelf approach fails to honour or respond to this context. Home interior design in Kolkata is most effective when it accounts for climate, heritage, space constraints, and cultural preferences."
  },
  {
    heading: "Final Thought: A Home Should Feel Like You",
    body: "Ready-made furniture fills a space. Custom interiors complete it. The difference is not just visual. It's the way a room feels when it's been thought through — when every drawer opens smoothly, every corner has a purpose, every material was chosen with your life in mind. If you're planning a new home or renovation in Kolkata, the best investment you can make is not in a specific piece of furniture — it's in a designer who will make every square foot work harder and feel better."
  }
];

const TRENDS_CONTENT = [
  {
    heading: "Heritage Meets Modern: The New Kolkata Aesthetic",
    body: "Old-world charm isn't going anywhere — it's getting a glow-up. Homeowners are pairing exposed brick, jaali-inspired partitions, and colonial-style windows with clean, minimal furniture. Think a classic Kolkata balcony reimagined with a sleek reading nook. It's a trend perfectly suited to the city's older housing stock in areas like Bhowanipore and North Kolkata, where original architectural details are a selling point, not a flaw.\n\nSearch tip: People looking for this style often search 'heritage home interior Kolkata' or 'colonial style interior design India.'"
  },
  {
    heading: "Humidity-Smart Materials Are Non-Negotiable",
    body: "Kolkata's monsoons and humidity don't play nice with the wrong materials. That's why 2026 is seeing a shift toward moisture-resistant laminates, WPC (wood-plastic composite) panels, and breathable fabric choices over traditional solid wood in high-humidity zones like kitchens and bathrooms. It's practical design that actually respects the local climate — something generic 'top trends' lists from other cities miss entirely."
  },
  {
    heading: "Compact, Multi-Functional Furniture for Apartment Living",
    body: "With apartment sizes in New Town and Rajarhat getting smaller but smarter, foldable dining tables, bed-cum-storage units, and modular wardrobes are dominating searches. This isn't about compromising on style — it's about designing every square foot to earn its place.\n\nSearch tip: 'Space-saving furniture Kolkata' and 'small flat interior design ideas' are high-intent, high-volume local searches worth targeting."
  },
  {
    heading: "Earthy, Warm Palettes Over Stark White",
    body: "Cool greys and pure whites are giving way to terracotta, warm ochre, sage green, and deep browns — colours that pair beautifully with Kolkata's natural light and reflect a growing preference for interiors that feel cozy rather than clinical."
  },
  {
    heading: "Vastu-Conscious Layouts, Designed Discreetly",
    body: "Vastu remains deeply important to a large segment of Kolkata homeowners, and 2026's approach is subtler than ever — designers are weaving Vastu principles (like the placement of the pooja corner or the direction of the main door) into contemporary layouts without making the home feel dated. It's tradition and trend, working together."
  },
  {
    heading: "Why Local Search Matters for Your Interior Design Project",
    body: "If you're planning a renovation, chances are you're not just searching 'interior design trends' — you're searching 'interior designer near me,' 'best interior designer in Kolkata,' or 'affordable home interior Kolkata.' Google increasingly favours content that speaks directly to a homeowner's actual city, climate, and housing type — which is exactly why generic, one-size-fits-all design blogs underperform for local readers."
  },
  {
    heading: "Ready to Bring These Trends Home?",
    body: "Whether you're drawn to heritage-modern fusion or want a Vastu-friendly, humidity-smart makeover, the right interior designer in Kolkata can help you translate these trends into a space that's uniquely yours.\n\nGet in touch with Decodia today for a free consultation and let's design a home that fits your life — and your city."
  }
];

// ─── 2 BHK INTERIOR DESIGN IN KOLKATA ────────────────────────────────────────
const BHK_INTERIOR_CONTENT = [
  {
    heading: "What Does 2 BHK Interior Design Include?",
    body: "Most 2 BHK packages cover the same core work. Ask any designer to list what is inside their quote:\n\n• Living and dining furniture, TV unit and false ceiling\n• Modular kitchen with chimney and hob space\n• Wardrobes and beds for both bedrooms\n• Lighting, electrical points and painting\n• Bathroom fittings, only if you choose a full renovation"
  },
  {
    heading: "2 BHK Layout Ideas, Room by Room",
    body: "Living Room and Dining — In a compact flat, keep the living and dining area open to each other. A wall-mounted TV unit, a sofa with hidden storage and a foldable or 4-seater dining table keep the floor free. Light colours and one accent wall make a small hall feel bigger.\n\nKitchen — An L-shaped or parallel modular kitchen suits most 2 BHK plans. Plan the cooking triangle (hob, sink, fridge) first and add tall units for storage. In Kolkata, choose moisture-resistant carcass material for the sink and hob side.\n\nMaster Bedroom — A sliding-door wardrobe saves space when the room is small. Keep the bed against the solid wall and add bedside storage instead of separate tables.\n\nSecond Bedroom or Home Office — Many families now turn the second bedroom into a guest room plus work-from-home corner. A wall bed or a fold-down desk lets the same room do both jobs."
  },
  {
    heading: "2 BHK Interior Design Cost in Kolkata",
    body: "Cost depends on your carpet area, material quality and how much furniture is fixed versus loose. Use the ranges below as a starting point and see our full interior design cost guide for Kolkata for the breakup.\n\nBasic Package — Essential modular work, standard laminates, basic lighting\n\nStandard Package — Better finishes, false ceiling, full wardrobes\n\nPremium Package — Custom design, premium hardware, designer lighting\n\nNote: fill these with your own current rates before publishing. Prices change with material and market."
  },
  {
    heading: "How Long Does 2 BHK Interior Work Take?",
    body: "Design and approvals usually take 2 to 3 weeks. Execution commonly runs 6 to 10 weeks depending on scope. Deciding materials early is the biggest way to avoid delay."
  },
  {
    heading: "Kolkata-Specific Tips for Your Flat",
    body: "• Humidity and monsoon: use moisture-resistant plywood or boards for kitchen and bathroom-side wardrobes\n• Seepage check: inspect walls and ceilings before fixing wooden work, especially in older buildings\n• Termite protection: ask for anti-termite treatment on wooden work\n• Ventilation: keep window and exhaust points free, do not block them with wardrobes\n• Building rules: check your housing society or association rules on working hours and debris removal before work starts"
  },
  {
    heading: "1 BHK and 3 BHK: What Changes?",
    body: "• 1 BHK: invest in multi-use furniture, such as a sofa cum bed and wall-to-wall storage\n• 3 BHK: the third room can be a study, prayer room or guest room, so plan lighting and storage early"
  },
  {
    heading: "Common 2 BHK Interior Mistakes to Avoid",
    body: "• Buying furniture before finalising the layout\n• Ignoring storage until the flat feels crowded\n• Choosing dark colours in a small, low-light flat\n• Not asking for a written material list and warranty\n• Skipping site measurement by the designer"
  },
  {
    heading: "Planning Your 2 BHK Interior in Kolkata?",
    body: "Decodia Design Studio designs homes across Kolkata. Share your floor plan and get a free consultation. Call or Whatsapp - +91 8100364941 / +91 83708 25457"
  }
];

const BHK_INTERIOR_FAQS = [
  {
    q: "How much does 2 BHK interior design cost in Kolkata?",
    a: "It depends on carpet area, materials and scope. See the cost table above and our detailed cost guide for a room-wise breakup."
  },
  {
    q: "How long does 2 BHK interior work take?",
    a: "Usually 6 to 10 weeks after design approval, depending on scope."
  },
  {
    q: "Should I hire an interior designer or a contractor?",
    a: "A designer plans layout, materials and budget together, while a contractor only executes. Read our guide on how to hire an interior designer in Kolkata to compare."
  },
  {
    q: "Can I finish interiors in phases?",
    a: "Yes. Do kitchen, wardrobes and electrical first, then add loose furniture and decor later."
  },
  {
    q: "Do I need permission from my housing society?",
    a: "Usually you need to inform them and follow their working-hour and debris rules. Check with your association."
  }
];

// ─── POSTS WITH BLOG POSTS ───────────────────────────────────────────────────
const POSTS = [
  {
    id: 0,
    title: "Why Interior Design Is Important: More Than Just a Pretty Space",
    excerpt: "We spend nearly 90% of our lives indoors — yet most of us never ask: does this space actually work for us? From mood and wellbeing to property value and sustainability, discover why good interior design is one of the most practical investments you can make in Kolkata.",
    category: "Tips & Tricks",
    date: "15 Feb 2026",
    readTime: "5 min read",
    author: { name: "Decodia Design Studio", avatar: "DS" },
    featured: true,
    hot: true,
    image: blog_image1,
    fullContent: FULL_FEATURED_CONTENT,
    designTag: null,
  },
  {
    id: 1,
    title: "Why Custom Interiors Feel Better Than Ready-Made Furniture",
    excerpt: "You finally move into your new apartment. You've spent weekends at furniture stores, picked out the sofa that looked perfect in the showroom — and yet, something feels off. Discover why custom interiors complete your home in ways ready-made furniture never can.",
    category: "Tips & Tricks",
    date: "20 May 2026",
    readTime: "6 min read",
    author: { name: "Decodia Design Studio", avatar: "DS" },
    featured: false,
    hot: true,
    image: secondBlogImage,
    fullContent: CUSTOM_INTERIORS_CONTENT,
    designTag: "Design with PURPOSE",
  },
  {
    id: 2,
    title: "2026 Interior Design Trends Homeowners Are Loving Right Now",
    excerpt: "Scrolling through Pinterest at 1 AM, wondering why your new flat doesn't look like the ones online? You're not alone. Discover 2026's biggest interior design trends — from heritage-modern fusion to humidity-smart materials — that are actually achievable and perfect for Kolkata homes.",
    category: "Trends",
    date: "29 Aug 2026",
    readTime: "5 min read",
    author: { name: "Decodia Design Studio", avatar: "DS" },
    featured: true,
    hot: true,
    image: trendsBlogImage,
    fullContent: TRENDS_CONTENT,
    designTag: "Trends 2026",
  },
  {
    id: 3,
    title: "2 BHK Interior Design in Kolkata: Layout Ideas and Cost Guide",
    excerpt: "Planning 2 BHK interior design in Kolkata? Get room-wise layout ideas, cost ranges, timeline and tips for humid weather. Book a free consultation.",
    category: "Spaces",
    date: "10 Oct 2026",
    readTime: "7 min read",
    author: { name: "Decodia Design Studio", avatar: "DS" },
    featured: false,
    hot: true,
    image: blog_image_new,
    fullContent: BHK_INTERIOR_CONTENT,
    faqs: BHK_INTERIOR_FAQS,
    designTag: "2 BHK Guide",
    seo: {
      focusKeyword: "2 bhk interior design kolkata",
      secondaryKeywords: [
        "2 bhk interior design",
        "flat interior design kolkata",
        "1 bhk interior design",
        "3 bhk interior design",
        "2 bhk interior design cost"
      ],
      titleTag: "2 BHK Interior Design in Kolkata: Ideas, Layout & Cost",
      metaDescription: "Planning 2 BHK interior design in Kolkata? Get room-wise layout ideas, cost ranges, timeline and tips for humid weather. Book a free consultation.",
      slug: "/2-bhk-interior-design-kolkata/",
      imageAlt: "Modern 2 BHK living room interior design in a Kolkata flat",
      schema: ["Article", "FAQPage"]
    }
  }
];

const POSTS_PER_PAGE = 6;

const CAT_COLOUR = {
  "Tips & Tricks": "#FF6B35",
  Trends: "#008C9E",
  Materials: "#695424",
  Colour: "#9B59B6",
  Spaces: "#27AE60",
  Renovation: "#E67E22",
  Budgeting: "#C0392B",
};

const avatarBg = (initials) => {
  const map = { PS: "#FF6B35", AM: "#008C9E", RB: "#695424", DS: "#b77446" };
  return map[initials] || "#b77446";
};

// ─── NAVIGATION COMPONENT ────────────────────────────────────────────────────
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openForm, setOpenForm] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const { menuItems } = useSelector((state) => state.navigation);
  const currentPath = location.pathname;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setScrolled(currentScrollY > 50);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleNavigation = (path, label) => {
    navigate(path);
    dispatch(setCurrentPage(label));
    setDrawerOpen(false);
  };

  const desktopMenu = (
    <Box sx={{
      display: "flex",
      flexDirection: "row",
      gap: 4,
      alignItems: "center",
    }}>
      {menuItems && menuItems.map((item) => (
        <Button
          key={item.id}
          onClick={() => handleNavigation(item.path, item.label)}
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: "0.95rem",
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "auto",
            '&:hover': { color: "#EED688" }
          }}
        >
          {item.label}
        </Button>
      ))}
      <Button
        variant="contained"
        onClick={() => setOpenForm(true)}
        sx={{
          background: "linear-gradient(45deg, #FF6B35, #FF8E53)",
          color: "white",
          fontWeight: 700,
          borderRadius: "8px",
          padding: "8px 20px",
          whiteSpace: "nowrap",
          minWidth: "auto",
        }}
      >
        <ChatBubbleIcon sx={{ fontSize: 18, mr: 1 }} />
        FREE CONSULTATION
      </Button>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: scrolled ? "rgba(61, 56, 56, 0.2)" : "transparent",
        backdropFilter: scrolled ? "blur(10px)" : "none",
        zIndex: 1300,
        transition: "transform 0.3s ease-in-out",
        transform: isVisible ? "translateY(0)" : "translateY(-100%)",
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 25 }, pt: 2 }}>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 70,
            width: "100%",
          }}
        >
          <Box
            onClick={() => handleNavigation("/", "Home")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <img src={LogoImage} alt="DECODIA" style={{ height: 55, width: "auto" }} />
          </Box>

          {!isMobile && desktopMenu}

          {isMobile && (
            <>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "white" }}>
                <MenuIcon />
              </IconButton>
              <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 250, p: 2 }}>
                  <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
                    <IconButton onClick={() => setDrawerOpen(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  {menuItems && menuItems.map((item) => (
                    <Button
                      key={item.id}
                      fullWidth
                      onClick={() => handleNavigation(item.path, item.label)}
                      sx={{
                        py: 2,
                        color: "black",
                        justifyContent: "flex-start",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setOpenForm(true);
                      setDrawerOpen(false);
                    }}
                    sx={{
                      mt: 2,
                      background: "linear-gradient(45deg, #FF6B35, #FF8E53)",
                      color: "white",
                    }}
                  >
                    FREE CONSULTATION
                  </Button>
                </Box>
              </Drawer>
            </>
          )}
        </Toolbar>
      </Container>
      <ConsultationFormDialog open={openForm} onClose={() => setOpenForm(false)} />
    </AppBar>
  );
};

// ─── CATEGORY PILL COMPONENT ─────────────────────────────────────────────────
function CategoryPill({ label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2.5,
        py: 1,
        borderRadius: "100px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: active ? 600 : 400,
        fontSize: "0.875rem",
        color: active ? "#fff" : C.muted,
        background: active ? `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)` : "transparent",
        border: `1.5px solid ${active ? "transparent" : C.border}`,
        transition: "all 0.25s ease",
        "&:hover": {
          background: active ? `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)` : `rgba(255,107,53,0.08)`,
          color: active ? "#fff" : C.primary,
          borderColor: active ? "transparent" : C.primary,
        },
      }}
    >
      {label}
    </Box>
  );
}

function PostCard({ post, bookmarked, onBookmark, onSelect }) {
  const catColor = CAT_COLOUR[post.category] || C.primary;

  return (
    <Card
      onClick={() => onSelect && onSelect(post)}
      sx={{
        borderRadius: "29px",
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        boxShadow: "0 11px 36px rgba(26,16,5,0.11)",
        height: "100%",
        minHeight: { xs: 450, sm: 504, md: 468 },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        background: C.card,
        cursor: "pointer",
        transition: "all 0.45s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
          boxShadow: "0 45px 81px rgba(255,107,53,0.32)",
          transform: "translateY(-10.8px)",
        },
      }}
    >
      {/* IMAGE - LEFT SIDE */}
      <Box sx={{
        position: "relative",
        flexShrink: 0,
        width: { xs: "100%", sm: "47%" },
        height: { xs: 315, sm: "auto" },
        minHeight: { sm: 468 },
        overflow: "hidden",
        background: "#f0f0f0",
      }}>
        <CardMedia
          component="img"
          image={post.image}
          alt={post.title}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.72s ease",
            ".MuiCard-root:hover &": { transform: "scale(1.09)" },
          }}
        />
        <Box sx={{ position: "absolute", top: 22, left: 22, display: "flex", gap: 1.8 }}>
          <Chip
            label={post.category}
            size="medium"
            sx={{
              background: catColor,
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.81rem",
              height: 34,
              borderRadius: "11px",
              px: 1.8,
              boxShadow: "0 3.6px 10.8px rgba(0,0,0,0.18)",
            }}
          />
          {post.hot && (
            <Chip
              icon={<LocalFireDepartmentIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
              label="HOT"
              size="medium"
              sx={{
                background: "#E55A2B",
                color: "#fff",
                fontWeight: 800,
                fontSize: "0.77rem",
                height: 34,
                borderRadius: "11px",
                px: 1.35,
                boxShadow: "0 3.6px 10.8px rgba(0,0,0,0.18)",
              }}
            />
          )}
        </Box>
        <IconButton
          onClick={(e) => { e.stopPropagation(); onBookmark(post.id); }}
          sx={{
            position: "absolute",
            top: 18,
            right: 18,
            background: "rgba(255,255,255,0.98)",
            backdropFilter: "blur(7px)",
            width: 43,
            height: 43,
            boxShadow: "0 3.6px 10.8px rgba(0,0,0,0.13)",
            '&:hover': { background: "white", transform: "scale(1.07)" },
          }}
        >
          {bookmarked ? <BookmarkIcon sx={{ fontSize: 23, color: C.primary }} /> : <BookmarkBorderIcon sx={{ fontSize: 23, color: C.muted }} />}
        </IconButton>
      </Box>

      {/* TEXT CONTENT - RIGHT SIDE */}
      <CardContent sx={{ 
        p: 4.5, 
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}>
        <Stack direction="row" spacing={1.8} alignItems="center" sx={{ mb: 2.7 }}>
          <Avatar sx={{
            width: 47,
            height: 47,
            background: avatarBg(post.author.avatar),
            fontSize: "1.08rem",
            fontWeight: 800,
            boxShadow: "0 3.6px 9px rgba(0,0,0,0.09)",
          }}>
            {post.author.avatar}
          </Avatar>
          <Typography sx={{ fontSize: "0.9rem", color: C.muted, fontWeight: 600 }}>
            {post.author.name}
          </Typography>
          <Box sx={{ width: 5.4, height: 5.4, borderRadius: "50%", background: C.border }} />
          <Typography sx={{ fontSize: "0.9rem", color: C.muted, fontWeight: 500 }}>
            {post.date}
          </Typography>
        </Stack>

        {post.designTag && (
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 800,
              fontSize: "0.81rem",
              letterSpacing: "3.6px",
              color: C.primary,
              textTransform: "uppercase",
              mb: 2.25,
            }}
          >
            {post.designTag}
          </Typography>
        )}

        <Typography
          sx={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 800,
            fontSize: { xs: "1.44rem", sm: "1.62rem", md: "1.8rem" },
            color: C.text,
            lineHeight: 1.4,
            mb: 2.7,
          }}
        >
          {post.title}
        </Typography>

        <Typography sx={{
          fontSize: "0.95rem",
          color: C.muted,
          lineHeight: 1.73,
          mb: 3.15,
          display: { xs: "none", sm: "block" },
        }}>
          {post.excerpt.length > 180 ? `${post.excerpt.substring(0, 180)}...` : post.excerpt}
        </Typography>

        <Divider sx={{ borderColor: C.border, mb: 3.15 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.35} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 18, color: C.muted }} />
            <Typography sx={{ fontSize: "0.9rem", color: C.muted, fontWeight: 600 }}>
              {post.readTime}
            </Typography>
          </Stack>
          <Button
            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
            sx={{
              fontWeight: 800,
              fontSize: "0.9rem",
              color: C.primary,
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              '&:hover': { transform: "translateX(9px)", transition: "transform 0.27s ease" },
            }}
          >
            Read more
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
// ─── FEATURED POST CARD - IMAGE LEFT, TEXT RIGHT ─────────────────────────────
function FeaturedPostCard({ post, bookmarked, onBookmark, onSelect }) {
  const catColor = CAT_COLOUR[post.category] || C.primary;

  return (
    <Card
      onClick={() => onSelect && onSelect(post)}
      sx={{
        borderRadius: "32px",
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        boxShadow: "0 24px 48px rgba(26,16,5,0.12)",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        background: C.card,
        cursor: "pointer",
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
          boxShadow: "0 32px 64px rgba(255,107,53,0.25)",
          transform: "translateY(-8px)",
        },
      }}
    >
      {/* IMAGE - LEFT SIDE */}
      <Box sx={{
        position: "relative",
        flexShrink: 0,
        width: { xs: "100%", md: "50%" },
        height: { xs: 280, md: "auto" },
        minHeight: { md: 420 },
        overflow: "hidden",
        order: { xs: 0, md: 1 }
      }}>
        <CardMedia
          component="img"
          image={post.image}
          alt={post.title}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.6s ease",
            ".MuiCard-root:hover &": { transform: "scale(1.05)" },
          }}
        />
        <Box sx={{ position: "absolute", top: 20, left: 20, display: "flex", gap: 1.5 }}>
          <Chip
            label={post.category}
            sx={{
              background: catColor,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.8rem",
              height: 32,
              borderRadius: "10px",
            }}
          />
          {post.hot && (
            <Chip
              icon={<LocalFireDepartmentIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
              label="HOT"
              sx={{
                background: "#E55A2B",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 32,
                borderRadius: "10px",
              }}
            />
          )}
        </Box>
        <IconButton
          onClick={(e) => { e.stopPropagation(); onBookmark(post.id); }}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(4px)",
            width: 40,
            height: 40,
            '&:hover': { background: "white" },
          }}
        >
          {bookmarked ? <BookmarkIcon sx={{ fontSize: 22, color: C.primary }} /> : <BookmarkBorderIcon sx={{ fontSize: 22, color: C.muted }} />}
        </IconButton>
      </Box>

      {/* TEXT CONTENT - RIGHT SIDE */}
      <CardContent sx={{ 
        p: { xs: 3, md: 4.5 }, 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center", 
        flex: 1,
        order: { xs: 1, md: 2 }
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Avatar sx={{ width: 40, height: 40, background: avatarBg(post.author.avatar), fontSize: "0.9rem", fontWeight: 700 }}>
            {post.author.avatar}
          </Avatar>
          <Typography sx={{ fontSize: "0.85rem", color: C.muted, fontWeight: 500 }}>
            {post.author.name}
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: "50%", background: C.border }} />
          <Typography sx={{ fontSize: "0.85rem", color: C.muted }}>
            {post.date}
          </Typography>
        </Stack>

        <Typography
          sx={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            fontSize: { xs: "1.5rem", md: "2rem" },
            color: C.text,
            lineHeight: 1.3,
            mb: 2,
          }}
        >
          {post.title}
        </Typography>

        <Typography sx={{ fontSize: "0.95rem", color: C.muted, lineHeight: 1.7, mb: 2.5 }}>
          {post.excerpt}
        </Typography>

        <Divider sx={{ borderColor: C.border, mb: 2.5 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 18, color: C.muted }} />
            <Typography sx={{ fontSize: "0.85rem", color: C.muted }}>
              {post.readTime}
            </Typography>
          </Stack>
          <Button
            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
            sx={{
              fontWeight: 700,
              fontSize: "0.85rem",
              color: C.primary,
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              '&:hover': { transform: "translateX(6px)", transition: "transform 0.2s" },
            }}
          >
            Read more
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── SIDEBAR TRENDING CARD ───────────────────────────────────────────────────
function SidebarTrendingCard({ post, rank }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 1, borderBottom: `1px solid ${C.border}`, "&:last-child": { borderBottom: "none" }, cursor: "pointer", "&:hover .trend-title": { color: C.primary } }}>
      <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.2rem", fontWeight: 700, color: C.border, lineHeight: 1, minWidth: 24 }}>{String(rank).padStart(2, "0")}</Typography>
      <Box>
        <Chip label={post.category} size="small" sx={{ background: `${CAT_COLOUR[post.category] || C.primary}18`, color: CAT_COLOUR[post.category] || C.primary, fontWeight: 600, fontSize: "0.6rem", height: 18, borderRadius: "4px", mb: 0.5 }} />
        <Typography className="trend-title" sx={{ fontWeight: 600, fontSize: "0.75rem", color: C.text, lineHeight: 1.3 }}>{post.title}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.3 }}>
          <AccessTimeIcon sx={{ fontSize: 10, color: C.muted }} />
          <Typography sx={{ fontSize: "0.65rem", color: C.muted }}>{post.readTime}</Typography>
        </Stack>
      </Box>
    </Stack>
  );
}

// ─── ARTICLE MODAL ──────────────────────────────────────────────────────────
function ArticleModal({ post, onClose }) {
  if (!post) return null;
  const catColor = CAT_COLOUR[post.category] || C.primary;
  return (
    <Modal open={!!post} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 300, style: { backdropFilter: "blur(6px)", background: "rgba(26,16,5,0.75)" } } }}>
      <Fade in={!!post}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "90vw", sm: "80vw", md: "60vw" }, maxWidth: 700, maxHeight: "85vh", overflowY: "auto", background: C.surface, borderRadius: "20px", outline: "none" }}>
          <Box sx={{ position: "relative", height: { xs: 180, md: 240 }, overflow: "hidden", borderRadius: "20px 20px 0 0" }}>
            <img src={post.image} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(26,16,5,0.6), transparent)" }} />
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 14, right: 14, background: "rgba(0,0,0,0.5)", color: "#fff" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
            <Chip label={post.category} size="small" sx={{ position: "absolute", bottom: 16, left: 20, background: catColor, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
          </Box>
          <Box sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Avatar sx={{ width: 28, height: 28, background: avatarBg(post.author.avatar), fontSize: "0.6rem", fontWeight: 700 }}>{post.author.avatar}</Avatar>
              <Typography sx={{ fontSize: "0.7rem", color: C.muted, fontWeight: 500 }}>{post.author.name}</Typography>
              <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: C.border }} />
              <Typography sx={{ fontSize: "0.7rem", color: C.muted }}>{post.date}</Typography>
              <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: C.border }} />
              <Stack direction="row" spacing={0.5} alignItems="center"><AccessTimeIcon sx={{ fontSize: 12, color: C.muted }} /><Typography sx={{ fontSize: "0.7rem", color: C.muted }}>{post.readTime}</Typography></Stack>
            </Stack>
            <Typography variant="h4" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: { xs: "1.3rem", md: "1.6rem" }, color: C.text, lineHeight: 1.25, mb: 2.5 }}>{post.title}</Typography>
            <Divider sx={{ borderColor: C.border, mb: 2.5 }} />
            {post.fullContent ? (
              post.fullContent.map((section, i) => (
                <Box key={i} sx={{ mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "1rem", color: C.text, mb: 1 }}>{section.heading}</Typography>
                  <Typography sx={{ fontSize: "0.85rem", color: C.muted, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{section.body}</Typography>
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: "0.85rem", color: C.muted, lineHeight: 1.7 }}>{post.excerpt}</Typography>
            )}

            {/* FAQ SECTION */}
            {post.faqs && post.faqs.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: C.text,
                    mb: 1.5
                  }}
                >
                  Frequently Asked Questions
                </Typography>
                {post.faqs.map((faq, i) => (
                  <Box key={i} sx={{ mb: 1.8 }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "0.85rem", color: C.text, mb: 0.5 }}
                    >
                      {faq.q}
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: C.muted, lineHeight: 1.7 }}>
                      {faq.a}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ borderColor: C.border, mt: 3, mb: 2.5 }} />
            <Typography sx={{ fontStyle: "italic", fontSize: "0.75rem", color: C.muted, textAlign: "center" }}>By <strong style={{ color: C.primary }}>Decodia Design Studio</strong> — Kolkata's trusted interior design firm</Typography>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

// ─── MAIN BLOG PAGE ──────────────────────────────────────────────────────────
const Blog = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [bookmarks, setBookmarks] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [visible, setVisible] = useState(false);
  const heroRef = useRef(null);
  const [openForm, setOpenForm] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(timer);
  }, []);

  const handleBookmark = (id) => {
    setBookmarks((prev) => prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]);
  };

  const handleExploreArticles = () => {
    const phoneNumber = "918370825457";
    const message = "Hi! I'm interested in exploring your articles about interior design.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleViewOurWork = () => {
    navigate("/services");
  };

  const filtered = POSTS.filter((p) => {
    const matchesCat = activeCategory === "All" || p.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  const featuredPost = filtered.find((p) => p.featured) || filtered[0];
  const gridPosts = filtered.filter((p) => p.id !== featuredPost?.id);
  const totalPages = Math.ceil(gridPosts.length / POSTS_PER_PAGE);
  const paginated = gridPosts.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);
  const trending = POSTS.filter((p) => p.hot || p.featured).slice(0, 4);

  return (
    <>
      <style>{FONTS}</style>
      <Box sx={{ background: C.surface, minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" }}>

        <Navigation />

        {/* HERO SECTION */}
        <Box
          ref={heroRef}
          sx={{
            background: `linear-gradient(135deg, ${C.dark} 0%, ${C.mid} 60%, #2a1a06 100%)`,
            pt: { xs: 12, sm: 15, md: 18, lg: 35 },
            pb: { xs: 6, sm: 8, md: 10, lg: 12 },
            position: "relative",
            overflow: "hidden",
            minHeight: { xs: "auto", sm: "auto", md: "auto" },
            display: "flex",
            alignItems: "center",
          }}
        >
          <Container maxWidth="sm" sx={{ position: "relative", zIndex: 2, px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{
              opacity: visible ? 1 : 0,
              transform: visible ? "none" : "translateY(24px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "100%", md: "100%" },
              mx: "auto",
            }}>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.5rem", lg: "2.9rem" }, fontWeight: 600, color: "#ffffff", lineHeight: 1.3, mb: 1 }}>
                Your home should
              </Typography>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "2rem", sm: "2.2rem", md: "2.5rem", lg: "2.8rem" }, fontWeight: 800, color: C.primary, lineHeight: 1.2, mb: 1.5, fontStyle: "italic" }}>
                feel like You.
                <Box component="span" sx={{ display: "inline-block", width: { xs: 8, md: 10 }, height: { xs: 8, md: 10 }, borderRadius: "50%", background: C.primary, ml: 0.5, animation: "pulse 1.5s infinite" }} />
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: { xs: "0.85rem", md: "0.9rem", lg: "0.95rem" }, maxWidth: { xs: "100%", sm: "100%", md: "100%" }, lineHeight: 1.5, mb: 2.5, mx: "auto" }}>
                Expert perspectives on interior design, materials, colour theory, and the craft of building spaces that feel like home.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                <Button variant="contained" size="medium" onClick={handleExploreArticles} sx={{ background: `linear-gradient(45deg, ${C.primary}, ${C.primaryLight})`, color: "white", fontWeight: 700, borderRadius: "50px", px: { xs: 2.5, md: 3 }, py: { xs: 0.8, md: 1 }, fontSize: { xs: "0.8rem", md: "0.85rem" }, '&:hover': { transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(255, 107, 53, 0.3)" } }}>
                  Explore Articles
                </Button>
                <Button variant="outlined" size="medium" onClick={handleViewOurWork} sx={{ borderColor: "rgba(255,255,255,0.3)", color: "white", fontWeight: 600, borderRadius: "50px", px: { xs: 2.5, md: 3 }, py: { xs: 0.8, md: 1 }, fontSize: { xs: "0.8rem", md: "0.85rem" }, '&:hover': { borderColor: C.primary, backgroundColor: "rgba(255, 107, 53, 0.1)" } }}>
                  View Our Work
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* CATEGORY FILTERS */}
        <Box sx={{ background: "#fff", borderBottom: `1px solid ${C.border}` }}>
          <Container maxWidth="md">
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(8, auto)" }, gap: 1, py: 1.5, overflowX: { md: "auto" }, flexWrap: { md: "nowrap" } }}>
              {CATEGORIES.map((cat) => (
                <CategoryPill key={cat} label={cat} active={activeCategory === cat} onClick={() => { setActiveCategory(cat); setPage(1); }} />
              ))}
            </Box>
          </Container>
        </Box>

        {/* MAIN CONTENT */}
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              {/* Featured Article */}
              {featuredPost && filtered.length > 0 && !searchQuery && activeCategory === "All" && (
                <Box sx={{ mb: 8 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                    <FiberNewIcon sx={{ color: C.primary, fontSize: 26 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", textTransform: "uppercase", color: C.primary, letterSpacing: 1.5 }}>
                      Featured Article
                    </Typography>
                  </Stack>
                  <FeaturedPostCard
                    post={featuredPost}
                    bookmarked={bookmarks.includes(featuredPost.id)}
                    onBookmark={handleBookmark}
                    onSelect={setSelectedPost}
                  />
                </Box>
              )}
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "1.2rem", color: C.text, mb: 2 }}>
                {activeCategory === "All" ? "Latest Articles" : activeCategory}
                <Box component="span" sx={{ fontWeight: 400, fontSize: "0.8rem", color: C.muted, ml: 1 }}>({filtered.length})</Box>
              </Typography>
              {filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, border: `2px dashed ${C.border}`, borderRadius: "16px" }}>
                  <Typography sx={{ fontSize: "1.2rem", color: C.muted, mb: 1 }}>No articles found</Typography>
                  <Typography sx={{ color: C.muted, fontSize: "0.8rem" }}>Try a different search or category</Typography>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {paginated.map((post) => (
                    <Grid item xs={12} sm={6} key={post.id}>
                      <PostCard post={post} bookmarked={bookmarks.includes(post.id)} onBookmark={handleBookmark} onSelect={setSelectedPost} />
                    </Grid>
                  ))}
                </Grid>
              )}
              {totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 5 }}>
                  <Pagination count={totalPages} page={page} onChange={(_, v) => { setPage(v); window.scrollTo({ top: 400, behavior: "smooth" }); }} size="small" sx={{ "& .Mui-selected": { background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight}) !important`, color: "#fff !important" } }} />
                </Stack>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: "sticky", top: 90 }}>
                <Box sx={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: "14px", p: 2, mb: 3 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <TrendingUpIcon sx={{ color: C.primary, fontSize: 18 }} />
                    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "1rem", color: C.text }}>Trending Now</Typography>
                  </Stack>
                  {trending.map((p, i) => (
                    <SidebarTrendingCard key={p.id} post={p} rank={i + 1} />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* BOTTOM CTA */}
        <Box sx={{ background: `linear-gradient(135deg, ${C.warm} 0%, #8a5530 100%)`, py: { xs: 5, md: 6 }, textAlign: "center" }}>
          <Container maxWidth="sm">
            <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "#fff", fontSize: { xs: "1.4rem", md: "1.8rem" }, mb: 1.5 }}>Ready to Transform Your Space?</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem", mb: 3 }}>Our design team turns inspiration into reality. Book a free consultation and let's talk about your project.</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
              <Button variant="contained" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />} onClick={() => setOpenForm(true)} sx={{ background: "#fff", color: C.primaryDark, fontWeight: 700, fontSize: "0.8rem", borderRadius: "100px", px: 3, py: 1 }}>
                Book Free Consultation
              </Button>
              <Button variant="outlined" onClick={handleViewOurWork} sx={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff", fontWeight: 600, fontSize: "0.8rem", borderRadius: "100px", px: 3, py: 1 }}>
                View Our Work
              </Button>
            </Stack>
          </Container>
        </Box>

        <ArticleModal post={selectedPost} onClose={() => setSelectedPost(null)} />
      </Box>
      <ConsultationFormDialog open={openForm} onClose={() => setOpenForm(false)} />
      <ChatButton />
    </>
  );
};

export default Blog;